import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import cleanDeep from 'clean-deep';
import { useContext } from 'react';

import { ApplicationContext } from '~/shared/context/context';
import type {
    CourseHistoryCreation,
    CourseHistoryItem,
    CourseHistoryMutation,
} from '~/shared/data-contract/course-history';
import { courseHistoryItemArraySchema, courseHistoryItemSchema } from '~/shared/data-contract/course-history';
import type { Pathname } from '~/shared/http-fetcher/http-fetcher';
import { getUserHardSoftSkillsQueryKey } from '~/shared/query-hooks/hard-soft-skills';

import { nullifyEmptyStringValues } from '../../common/form';

export const useCourseHistoryQuery = (
    userId: string,
    enabled: boolean,
): UseQueryResult<readonly CourseHistoryItem[]> => {
    const {
        applicationState: { language },
        validatingCobrainerApiFetcher,
    } = useContext(ApplicationContext);
    const courseHistoryUrl: Pathname = `/users/${userId}/course-history`;

    return useQuery({
        enabled,
        queryFn: async () => {
            const result = await validatingCobrainerApiFetcher.get(courseHistoryItemArraySchema, {
                pathname: courseHistoryUrl,
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        queryKey: [courseHistoryUrl, 'GET', language.code],
    });
};

export const useOneCourseHistoryQuery = (
    userId: string,
    courseHistoryId: string,
    enabled: boolean,
): UseQueryResult<CourseHistoryItem> => {
    const {
        applicationState: { language },
        validatingCobrainerApiFetcher,
    } = useContext(ApplicationContext);
    const oneCourseHistoryUrl: Pathname = `/users/${userId}/course-history/${courseHistoryId}`;

    return useQuery({
        enabled,
        queryFn: async () => {
            const result = await validatingCobrainerApiFetcher.get(courseHistoryItemSchema, {
                pathname: oneCourseHistoryUrl,
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        queryKey: [oneCourseHistoryUrl, 'GET', language.code],
    });
};

export type CourseHistoryMutations = {
    readonly createCourseMutation: UseMutationResult<CourseHistoryItem, unknown, Omit<CourseHistoryItem, 'id'>>;
    readonly deleteCourseMutation: UseMutationResult<void, unknown, string>;
    readonly patchCourseMutation: UseMutationResult<CourseHistoryItem, unknown, CourseHistoryItem>;
};

export const useCourseHistoryMutations = (userId: string): CourseHistoryMutations => {
    const { cobrainerApiFetcher, validatingCobrainerApiFetcher } = useContext(ApplicationContext);
    const queryClient = useQueryClient();
    const pathname: Pathname = `/users/${userId}/course-history`;

    const invalidateCourseCache = (courseHistoryId: string): Promise<void> =>
        queryClient.invalidateQueries({
            queryKey: [`${pathname}/${courseHistoryId}`, 'GET'],
        });
    const invalidateCourseHistoryCache = async (): Promise<void> => {
        await queryClient.invalidateQueries({ queryKey: [pathname, 'GET'] });
        await queryClient.invalidateQueries({ queryKey: getUserHardSoftSkillsQueryKey(userId) });
    };

    const createCourseMutation = useMutation<CourseHistoryItem, unknown, Omit<CourseHistoryItem, 'id'>>({
        mutationFn: async (courseHistoryItem) => {
            const { courseTitle, skills, ...restValues } = courseHistoryItem;
            const data: CourseHistoryCreation = {
                ...cleanDeep(restValues),
                courseTitle,
                skillIds: skills?.map((skill) => skill.id) ?? [],
            };

            const result = await validatingCobrainerApiFetcher.postJson(courseHistoryItemSchema, {
                pathname,
                payload: data,
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        onSuccess: async (value) => {
            await invalidateCourseHistoryCache();
            await invalidateCourseCache(value.id);
        },
    });

    const patchCourseMutation = useMutation<CourseHistoryItem, unknown, CourseHistoryItem>({
        mutationFn: async (courseHistoryItem) => {
            const { id, courseTitle, skills, ...restValues } = courseHistoryItem;
            const data: CourseHistoryMutation = {
                ...cleanDeep(nullifyEmptyStringValues(restValues), { nullValues: false }),
                id,
                courseTitle,
                skillIds: skills?.map((skill) => skill.id) ?? [],
            };

            const result = await validatingCobrainerApiFetcher.patchJson(courseHistoryItemSchema, {
                pathname: `${pathname}/${courseHistoryItem.id}`,
                payload: data,
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        onSuccess: async (_, value) => {
            await invalidateCourseHistoryCache();
            await invalidateCourseCache(value.id);
        },
    });

    const deleteCourseMutation = useMutation<void, unknown, string>({
        mutationFn: async (courseHistoryId) => {
            const result = await cobrainerApiFetcher.delete({
                pathname: `/users/${userId}/course-history/${courseHistoryId}`,
            });

            if (result.isOk) {
                return;
            }

            throw result.error;
        },
        onSuccess: async () => {
            await invalidateCourseHistoryCache();
        },
    });

    return {
        createCourseMutation,
        deleteCourseMutation,
        patchCourseMutation,
    };
};
